/**
 * BrowseFilterBar — the owner's "filters on the top" (WS-K5).
 *
 * WS-K3 answered O-3 with a 232 px rail docked beside the results at ≥ 1280 and a drawer below
 * that. The owner's reply was about WHERE, not about WHAT:
 *
 *   > I want the filters to be on the top, so on the top you can find them. The search you can
 *   > put above. Then below it you can have small dropdowns — not as a side column.
 *
 * So the facet MODEL is untouched (`browseFilters.ts` is not edited by this wave) and the SHELL
 * is one shape at every width:
 *
 *   <section class="browse-filterbar" data-testid="browse-filters" aria-label="Filter templates">
 *     <input type="search" class="input browse-filterbar__q">          ← full width, on top
 *     <div class="browse-filterbar__row" role="group">                 ← the dropdown row
 *       <div class="browse-drop" data-facet="brand">
 *         <button class="browse-drop__btn" aria-expanded aria-haspopup="dialog" aria-controls>
 *         <div role="dialog" class="browse-drop__pop">                 ← the popover
 *           <fieldset class="browse-facet" data-facet="brand">
 *             <legend class="sr-only">Brand</legend>
 *             <label class="browse-facet__opt"><input type="checkbox"> Apple <span>12</span></label>
 *
 * Four decisions worth stating, because each is a place this could have gone wrong in a way
 * nothing would have caught:
 *
 * 1. **A real `<input type="checkbox">`, never `role="checkbox"`.** Unchanged from K3, and G-34
 *    still asserts zero fakes in `templates/`. A div pretending to be a checkbox loses Space,
 *    loses the form, loses the label association and loses the browser's own focus ring.
 * 2. **The keyboard ADDS, it does not replace.** Space and Tab stay native inside the popover;
 *    the bar adds ArrowUp/ArrowDown within the `<fieldset>` (no wrap, skipping disabled
 *    options), Home/End and Escape. Arrows do not toggle — that is radio-group behaviour.
 * 3. **`role="dialog"` WITHOUT `aria-modal`, and no focus trap.** This is a popover, not a
 *    drawer: Tab leaves it and the popover closes behind you, which is what every filter bar
 *    read for this wave does (M-K5-1 Etsy, M-K5-3 Uxcel). Claiming a modal that traps nothing is
 *    worse than claiming nothing — the same reasoning K3 used to keep `role` off its docked rail.
 * 4. **Nothing here owns the selection.** The bar is given `groups` (already counted) and
 *    `selection`, and reports intent through `onToggle` / `onClearGroup`. What it DOES own is
 *    disclosure — which group is open, which is showing all its options, each group's filter-box
 *    text — and that is presentation, which G-32 allows by name. It owns no search STATE either:
 *    the query lives in `TemplateListPage` exactly as it did before the field moved down here.
 */
import {
  useCallback, useEffect, useId, useMemo, useRef, useState,
  type FocusEvent as ReactFocusEvent, type KeyboardEvent,
} from "react";
import { Icon } from "../ui/Icon.js";
import type { FacetGroup, FacetKey, FacetSelection } from "./browseFilters.js";
import "./browse.css";

export interface BrowseFilterSearch {
  value: string;
  onChange: (next: string) => void;
  /** Doubles as the field's `aria-label`: a search box with no name announces nothing. */
  placeholder: string;
  /** `templates-search` on the admin surface, `ctpl-search` on the customer one (F-15). */
  testId: string;
}

export interface BrowseFilterBarProps {
  groups: FacetGroup[];
  selection: FacetSelection;
  onToggle: (key: FacetKey, value: string) => void;
  onClearGroup: (key: FacetKey) => void;
  /** The full-width field the owner asked to put above the dropdowns. */
  search: BrowseFilterSearch;
  testId?: string;
}

export function BrowseFilterBar({
  groups, selection, onToggle, onClearGroup, search, testId = "browse-filters",
}: BrowseFilterBarProps) {
  const uid = useId();
  /**
   * ONE open group at a time (M-K5-1 Etsy, M-K5-3 Uxcel).
   *
   * Two popovers open at once on a single row would overlap each other, and the second would
   * cover the first's options while both buttons read `aria-expanded="true"`.
   */
  const [open, setOpen] = useState<FacetKey | null>(null);
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [find, setFind] = useState<Record<string, string>>({});

  /** Group key → its dropdown button, so Escape and Done can hand focus back. */
  const btns = useRef(new Map<string, HTMLButtonElement | null>());
  /** Group key → its `.browse-drop` wrapper (button + popover), for the outside-click test. */
  const wraps = useRef(new Map<string, HTMLDivElement | null>());
  const pop = useRef<HTMLDivElement | null>(null);
  /** Set while a close is deliberate, so the focus-out handler does not fight it. */
  const closing = useRef(false);

  const close = useCallback((key: FacetKey | null, returnFocus: boolean) => {
    closing.current = true;
    setOpen(null);
    if (returnFocus && key) btns.current.get(key)?.focus();
    closing.current = false;
  }, []);

  /**
   * An outside press closes the open popover and leaves focus alone.
   *
   * `mousedown`, not `click`: a `click` listener fires after the pressed control has already
   * done its work, so pressing a *different* dropdown would open it and then this would close
   * it again. Presses INSIDE the open group's own wrapper are ignored — that is the button
   * itself, whose `onClick` owns the toggle, and the popover, which owns everything in it.
   */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const wrap = wraps.current.get(open);
      if (wrap && e.target instanceof Node && wrap.contains(e.target)) return;
      setOpen(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /**
   * Focus moves INTO the popover when it opens — onto the group's filter box if it has one,
   * otherwise onto the first option there is something to do with. A disclosure that opens and
   * leaves focus on the button costs a keyboard user an extra Tab for every group.
   */
  useEffect(() => {
    if (!open) return;
    const el = pop.current?.querySelector<HTMLElement>(
      'input[type="search"], input[type="checkbox"]:not(:disabled), button:not(:disabled)',
    );
    el?.focus();
  }, [open]);

  /**
   * Arrow / Home / End / Escape, scoped to the `<fieldset>` the event came from.
   *
   * Read off the DOM rather than an index, deliberately: the visible option list is whatever the
   * cap and the filter box currently leave, so an index into `group.options` would move focus to
   * an option that is not on screen the moment either is in play.
   */
  const onOptionKey = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    const set = e.currentTarget.closest("fieldset");
    if (!set) return;
    /* Disabled options are skipped rather than landed on: a zero-count option stays VISIBLE
       (M7 — the count is information) but it is not something a keyboard user can act on, and
       parking focus on a dead control is the keyboard equivalent of a button that does
       nothing. `:not(:disabled)` is the whole of it. */
    const boxes = [...set.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not(:disabled)')];
    const i = boxes.indexOf(e.currentTarget);
    if (i < 0) return;
    let next = -1;
    // No wrap, on purpose: wrapping from the last option to the first inside a group reads as
    // "nothing happened" when the group is taller than the popover.
    if (e.key === "ArrowDown") next = Math.min(i + 1, boxes.length - 1);
    else if (e.key === "ArrowUp") next = Math.max(i - 1, 0);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = boxes.length - 1;
    else return;
    e.preventDefault();
    boxes[next]?.focus();
  }, []);

  /** Escape anywhere in the popover closes it and hands focus back to its button. */
  const onPopKey = useCallback((e: KeyboardEvent<HTMLElement>, key: FacetKey) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    close(key, true);
  }, [close]);

  /**
   * Focus leaving the popover closes it — the non-modal half of decision 3.
   *
   * `relatedTarget` is where focus is GOING. It is `null` when focus left the document
   * altogether (a window blur), which must not close anything: the operator has not moved within
   * the page, and re-opening a popover they never dismissed is worse than leaving it.
   */
  const onPopBlur = useCallback((e: ReactFocusEvent<HTMLDivElement>) => {
    if (closing.current) return;
    const to = e.relatedTarget;
    if (!to) return;
    if (e.currentTarget.contains(to)) return;
    setOpen(null);
  }, []);

  /** Which options this group renders right now: the filter box, then the cap — with every
   *  SELECTED option force-included, so the control that removes an active filter is never
   *  hidden behind a cap the operator has no reason to lift. */
  const visibleOptions = useMemo(() => {
    const out = new Map<string, FacetGroup["options"]>();
    for (const g of groups) {
      const query = (find[g.key] ?? "").trim().toLowerCase();
      const matched = query ? g.options.filter((o) => o.label.toLowerCase().includes(query)) : g.options;
      if (showAll[g.key] || query || matched.length <= g.visibleCap) {
        out.set(g.key, matched);
        continue;
      }
      const capped = matched.slice(0, g.visibleCap);
      const seen = new Set(capped.map((o) => o.value));
      for (const o of matched) if (o.selected && !seen.has(o.value)) capped.push(o);
      out.set(g.key, capped);
    }
    return out;
  }, [groups, showAll, find]);

  return (
    <section className="browse-filterbar" data-testid={testId} aria-label="Filter templates">
      {/* ONE search box, ONE field set, ONE placeholder, on both surfaces (F-15, MIN-6) — it has
          simply moved out of the toolbar and onto the top of the thing it narrows. */}
      <input
        type="search"
        className="input browse-filterbar__q"
        placeholder={search.placeholder}
        aria-label={search.placeholder}
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
        data-testid={search.testId}
      />

      <div className="browse-filterbar__row" role="group" aria-label="Filter groups">
        {groups.map((g) => {
          const popId = `${uid}-drop-${g.key}`;
          const empty = g.options.length === 0;
          const isOpen = !empty && open === g.key;
          /* Counted off the SELECTION, not off the option flags: the selection is the model's
             answer and an option list is a render of it. They agree today (`facetOptions` seeds
             an option for every selected value, at zero if no loaded row carries it), and
             counting the authority means they cannot stop agreeing here. */
          const selectedCount = selection[g.key].length;
          const shown = visibleOptions.get(g.key) ?? [];
          const query = (find[g.key] ?? "").trim();
          const capped = !showAll[g.key] && !query && g.options.length > g.visibleCap;
          return (
            <div
              className="browse-drop"
              data-facet={g.key}
              data-open={isOpen ? "true" : undefined}
              key={g.key}
              ref={(el) => { wraps.current.set(g.key, el); }}
            >
              <button
                type="button"
                ref={(el) => { btns.current.set(g.key, el); }}
                className={`browse-drop__btn${selectedCount > 0 ? " browse-drop__btn--on" : ""}`}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-controls={popId}
                disabled={empty || undefined}
                onClick={() => (isOpen ? close(g.key, true) : setOpen(g.key))}
                data-testid={`browse-facet-${g.key}`}
              >
                <span className="browse-facet__legend">{g.legend}</span>
                {/* "Brand 2" is the information — the count is not aria-hidden (M-K5-3). */}
                {selectedCount > 0 && <span className="browse-facet__n">{selectedCount}</span>}
                {/* A group with nothing in it says so rather than vanishing (M7 at its limit). */}
                {empty && <span className="browse-facet__empty">0</span>}
                <Icon name={isOpen ? "chevron-up" : "chevron-down"} size={16} />
              </button>

              {isOpen && (
                <div
                  id={popId}
                  className="browse-drop__pop"
                  role="dialog"
                  aria-label={g.legend}
                  ref={pop}
                  onKeyDown={(e) => onPopKey(e, g.key)}
                  onBlur={onPopBlur}
                  data-testid={`browse-facet-${g.key}-pop`}
                >
                  <fieldset className="browse-facet" data-facet={g.key}>
                    {/* The <legend> names the group for a screen reader; the visible name is the
                        dropdown button, so this is .sr-only rather than painted twice. */}
                    <legend className="sr-only">{g.legend}</legend>
                    {/* M-K5-3's in-popover filter box. Only above the threshold: a search box
                        over six options is a control that costs more than it saves. */}
                    {g.options.length > g.searchThreshold && (
                      <input
                        type="search"
                        className="input input--sm browse-facet__find"
                        placeholder={`Find ${g.legend.toLowerCase()}`}
                        aria-label={`Find ${g.legend.toLowerCase()}`}
                        value={find[g.key] ?? ""}
                        onChange={(e) => setFind((p) => ({ ...p, [g.key]: e.target.value }))}
                        data-testid={`browse-facet-${g.key}-search`}
                      />
                    )}
                    {shown.map((o) => (
                      <label
                        className="browse-facet__opt"
                        key={o.value}
                        data-on={o.selected ? "true" : undefined}
                        data-off={o.disabled ? "true" : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={o.selected}
                          disabled={o.disabled || undefined}
                          value={o.value}
                          onChange={() => onToggle(g.key, o.value)}
                          onKeyDown={onOptionKey}
                          data-testid={`browse-facet-${g.key}-${o.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                        <span className="browse-facet__label">{o.label}</span>
                        <span className="browse-facet__count">{o.count}</span>
                      </label>
                    ))}
                    {!shown.length && <p className="browse-facet__none">No matches</p>}
                    {/* .link-btn is already a named 20px height exception WITH a 32px ring (K2),
                        so this needs no new rule and adds nothing to HEIGHT_EXCEPTIONS. */}
                    {(capped || showAll[g.key]) && (
                      <button
                        type="button"
                        className="link-btn browse-facet__more"
                        onClick={() => setShowAll((p) => ({ ...p, [g.key]: !p[g.key] }))}
                        data-testid={`browse-facet-${g.key}-more`}
                      >
                        {showAll[g.key] ? "Show fewer" : `Show all (${g.options.length})`}
                      </button>
                    )}
                  </fieldset>

                  <div className="browse-drop__foot">
                    {/* F-23's rule: a control that would do nothing is not rendered. `Clear all`
                        is deliberately absent — the chip row directly below this bar carries the
                        only one on screen (K3 review-1 m-3). */}
                    {selectedCount > 0 && (
                      <button
                        type="button"
                        className="link-btn browse-facet__clear"
                        onClick={() => onClearGroup(g.key)}
                        data-testid={`browse-facet-${g.key}-clear`}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost browse-drop__done"
                      onClick={() => close(g.key, true)}
                      data-testid={`browse-facet-${g.key}-done`}
                    >
                      <Icon name="check" size={16} /> Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
